// React & Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// Types & Constants
import {
  VehicleDriverSearchPageCmp,
  VehicleDriverSearchPageProps,
} from "./types";
import {
  FleetVehicleDriverRow,
  FleetVehicleDriverSearchFormValues,
} from "services/api/fleet/vehicles/drivers/types";
import { initialFleetVehicleDriverSearchForm } from "services/api/fleet/vehicles/drivers/consts";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { FLEET_VEHICLE_DRIVER_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// API
import {
  deleteFleetVehicleDriverAPI,
  getFleetVehicleDriversAPI,
} from "services/api/fleet/vehicles/drivers/api";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";

// Search & Pagination
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// UI Components
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";

// Navigation
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Local Components
import DriverSearchFilters from "ui/modules/fleet/drivers/pages/DriverSearchPage/components/DriverSearchFilters/DriverSearchFilters";
import VehicleDriverSearchResults from "./components/VehicleDriverSearchResults/VehicleDriverSearchResults";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";
import VehicleDriverEditCard from "../VehicleDriverEditCard/VehicleDriverEditCard";
import { invalidateDrivers } from "ui/components/Dropdown/DriverDropdown/DriverDropdown";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";

const VehicleDriverSearchPage: VehicleDriverSearchPageCmp = (
  props: VehicleDriverSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleDriverRow>
  >(initialDataWithPagination);

  // Filtri se pamte ovdje, pa ih zadrže i paginacija i osvježavanje nakon unosa.
  const [filterValues, setFilterValues] = useState<FleetVehicleDriverSearchFormValues>(
    initialFleetVehicleDriverSearchForm
  );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [driverId, setDriverId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Vozač koji čeka potvrdu brisanja.
  const [deleteDriverId, setDeleteDriverId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);

  // Zaglavlje prikazuje vozilo (slika i naziv), pa se dohvaća uz popis vozača.
  const fetchVehicle = async (vehicleId: string) => {
    try {
      const result = await getFleetVehicleAPI(vehicleId);
      setFormValues(result);
    } catch {
      setFormValues(initialFleetVehicleDetailForm);
    }
  };

  const fetchSearchResult = async (
    id: string,
    pagination: Pagination,
    values: FleetVehicleDriverSearchFormValues = filterValues
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleDriversAPI(pagination, id, values);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setDriverId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setDriverId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);

    setDriverId("");
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback(
    (values: FleetVehicleDriverSearchFormValues) => {
      setFilterValues(values);
      if (id) fetchSearchResult(id, searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, searchResults.pagination]
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteDriverId(id);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!id || !deleteDriverId) return;

    try {
      await deleteFleetVehicleDriverAPI(deleteDriverId, t);
      // Obrisani vozač mora nestati i iz dropdowna na premještaju i statusu.
      invalidateDrivers();
      fetchSearchResult(id, searchResults.pagination);
    } catch (err) {
      console.error("Greška prilikom brisanja vozača:", err);
    }

    setDeleteDriverId(null);
  }, [id, deleteDriverId, t, searchResults.pagination]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDriverId(null);
  }, []);

  // Vozač je vezan uz vozilo, ali popis nosi karticu vozila kao i ostale pretrage.
  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();

      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  // Promjena statusa preko značke — vozilo se dohvaća ponovno da se značka osvježi.
  const handleStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title={t(FLEET_VEHICLE_DRIVER_SEARCH_PAGE_TITLE.text)}
            icon={FLEET_VEHICLE_DRIVER_SEARCH_PAGE_TITLE.icon}
            iconColor={FLEET_VEHICLE_DRIVER_SEARCH_PAGE_TITLE.iconColor}
            onStatusChange={handleStatusChange}
          >
            <Button
              variant="primary"
              children={t("admin.vehicle.driver.search.add")}
              startIcon="/media/icons/plus.svg"
              onClick={handleAddNew}
            />
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          {/* Isti filteri kao u pregledu svih vozača, bez vozila, jer je vozilo već zadano. */}
          <DriverSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
            showVehicleFilter={false}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t("admin.fleet.vehicle.driver.search.noresults")}
            />
          ) : (
            <VehicleDriverSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onVehicleClick={handleVehicleClick}
              onDeleteClick={handleDeleteClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <VehicleDriverEditCard
          show={showAddNewModal}
          vehicleId={id}
          driverId={driverId}
          onSuccess={handleEditSuccess}
          onClose={() => setShowAddNewModal(false)}
        />
      )}

      <Dialog
        open={!!deleteDriverId}
        title="Brisanje vozača"
        message="Jeste li sigurni da želite obrisati vozača?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

VehicleDriverSearchPage.route =
  FleetVehicleRoutes.FleetVehicleDriverSearchPageRouter;

export default VehicleDriverSearchPage;
